import React, {FC, useMemo} from "react";
import {useTranslate} from "@tolgee/react";
import {FullProduct} from "ts/api/product";
import {useParams} from "react-router-dom";
import {useExhibitor} from "hooks";

import Tabs from "view/components/Tabs";
import Tab from "view/components/Tab";
import {PaymentMethodType} from "ts/api/paymentMethod";
import isBankTransferAvailable from "tools/order/isBankTransferAvailable";
import {useSelector} from "react-redux";
import {
  selectBillingDetails,
  selectTotalAmount,
} from "store/exhibitors/cartStepper/slice";
import {ExpandedOrder} from "ts/api/order";
import IDEAL from "view/apps/Exhibitor/pages/Cart/components/Payment/components/IDEAL";
import FlexBox from "view/components/Layout/FlexBox";
import PaymentButton from "view/apps/Exhibitor/pages/Cart/components/Payment/components/PaymentButton";
import CardIcon from "view/images/CardIcon";
import Card from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card";
import {
  Currency,
  IExhibitorPair,
  IOrganiser,
  IShow,
} from "@evennt-tech/api-v2-core";
import {useShoppingSummaryStateContext} from "view/components/ShoppingSummary/context";

import {generateTestId} from "tools/cypress/generateTestId";
import IDEALLogo from "assets/images/show/IDEALLogo.png";
import getOrderDescription, {
  getBankTransferOrderDescription,
} from "view/apps/Exhibitor/pages/Cart/helpers/getOrderDescription";
import {useStyles} from "./style";
import BankTransfer from "./components/BankTransfer";
import {CartFlow, CartFlowType} from "./types";
import {PlaceOrderCallback} from "../types";
import getOrderTransferDescription from "../../helpers/getOrderTransferDescription";
import {calculateTotal, paymentMethods, tabs} from "./shared";

type PaymentProps = {
  paymentMethodType: PaymentMethodType;
  changePaymentMethodType: (type: PaymentMethodType) => void;
  products: FullProduct[];
  onPlaceOrder: PlaceOrderCallback;
  organiser: IOrganiser;
  show: IShow;
  exhibitorPair: IExhibitorPair;
  order?: ExpandedOrder;
  isBankTransferAllowedByOrder?: boolean;
  isIdealPaymentFailed?: boolean;
};

const Payment: FC<PaymentProps> = ({
  paymentMethodType,
  changePaymentMethodType,
  show,
  products,
  exhibitorPair,
  organiser,
  onPlaceOrder,
  isBankTransferAllowedByOrder = false,
  isIdealPaymentFailed = false,
}) => {
  const {orderId} = useParams<{orderId?: string}>();
  const classes = useStyles();
  const {t} = useTranslate();

  const cartTotalAmount = useSelector(selectTotalAmount);
  const exhibitor = useExhibitor();

  const billingDetails = useSelector(selectBillingDetails);
  const {activeOrderGroup} = useShoppingSummaryStateContext();

  const billingDetailsString = useMemo(
    () =>
      paymentMethodType === PaymentMethodType.bankTransfer
        ? getBankTransferOrderDescription({
            tradingName: billingDetails.tradingName,
            name: billingDetails.name,
            status: billingDetails.status,
          })
        : getOrderDescription(billingDetails),
    [billingDetails, paymentMethodType]
  );
  const showName = show?.name || "";
  const companyName = exhibitorPair?.companyToShow?.company?.name || "";
  const transferredPaymentDescription = getOrderTransferDescription(
    showName,
    companyName,
    exhibitorPair?.stand?.name || ""
  );
  const orderDescription = `Show Name: ${showName};\n Exhibitor: ${companyName};\n Billing details: \n${billingDetailsString}`;

  const cartFlowProps: CartFlow = orderId
    ? {type: CartFlowType.created, orderId, products}
    : {type: CartFlowType.local, orderId: undefined, products};

  const isEuroCurrency = show.currency === Currency.eur;

  const cardComponent = () => {
    return (
      <Card
        {...cartFlowProps}
        onPlaceOrder={onPlaceOrder}
        orderDescription={orderDescription}
        transferredPaymentDescription={transferredPaymentDescription}
        show={show}
        exhibitorPair={exhibitorPair}
        organiser={organiser}
      />
    );
  };
  const idealComponent = () => {
    return (
      <IDEAL
        {...cartFlowProps}
        onPlaceOrder={onPlaceOrder}
        orderDescription={orderDescription}
        show={show}
        exhibitorPair={exhibitorPair}
        transferredPaymentDescription={transferredPaymentDescription}
        organiser={organiser}
        isPaymentFailed={isIdealPaymentFailed}
      />
    );
  };
  const handleTabNumberChange = (value: number) => {
    changePaymentMethodType(paymentMethods[value < 0 ? 0 : value]);
  };

  const isAnyProductRequiresApproval = useMemo(
    () => !!products.find((product) => product.approval),
    []
  );

  const renderPaymentButtons = () => {
    if (!isEuroCurrency) {
      return null;
    }
    return (
      <FlexBox
        gap={1}
        alignCenter
        mt={2}
        {...generateTestId("exhibitor-cart-payment-btns")}
      >
        {!activeOrderGroup && (
          <PaymentButton
            active={paymentMethodType === PaymentMethodType.cardPayment}
            icon={<CardIcon className={classes.cardIcon} />}
            label={t("exhibitor.cart.payment.tab.card")}
            onClick={() =>
              changePaymentMethodType(PaymentMethodType.cardPayment)
            }
          />
        )}
        {!isAnyProductRequiresApproval && (
          <PaymentButton
            active={paymentMethodType === PaymentMethodType.idealPayment}
            icon={<img src={IDEALLogo} alt="IDEAL" />}
            label="iDEAL"
            onClick={() =>
              changePaymentMethodType(PaymentMethodType.idealPayment)
            }
          />
        )}
      </FlexBox>
    );
  };

  const renderTabsView = () => {
    const totalAmount = calculateTotal(cartTotalAmount);

    const isCardPayment = PaymentMethodType.cardPayment === paymentMethodType;
    const shouldRenderIDEAL =
      paymentMethodType === PaymentMethodType.idealPayment &&
      isEuroCurrency &&
      !isAnyProductRequiresApproval;

    const canPayByBankTransfer = isBankTransferAvailable({
      currency: show.currency,
      enableBankTransferPaymentMethod: show.enableBankTransferPaymentMethod,
      bankTransferMinimalOrderValue: show.bankTransferMinimalOrderValue,
      totalAmount,
      isAllowedByOrder: isBankTransferAllowedByOrder,
    });

    const currentTab = paymentMethods.findIndex(
      (paymentMethod) => paymentMethod === paymentMethodType
    );
    const tabsValue = currentTab < 0 ? 0 : currentTab;

    // One shape whether bank transfer is offered or not. Paying the first of
    // several orders lowers the total, which can drop it under the bank
    // transfer minimum; a layout that moved `Card` to another child position
    // remounted it there and lost its order tabs mid-payment (FS-3335).
    return (
      <>
        {canPayByBankTransfer && !activeOrderGroup && (
          <Tabs<number>
            {...generateTestId("exhibitor-cart-payment-tabs")}
            value={tabsValue}
            classes={{
              flexContainer: classes.tabsFlexContainer,
            }}
            onChange={handleTabNumberChange}
          >
            {tabs.map((tab) => (
              <Tab
                className={classes.tab}
                classes={{selected: classes.selectedTab, root: classes.tabRoot}}
                label={t(tab.label)}
                key={tab.label}
              />
            ))}
          </Tabs>
        )}
        {(!canPayByBankTransfer || tabsValue === 0) && renderPaymentButtons()}
        {shouldRenderIDEAL && idealComponent()}
        {isCardPayment && cardComponent()}
        {canPayByBankTransfer &&
          paymentMethodType === PaymentMethodType.bankTransfer &&
          exhibitor && (
            <BankTransfer
              {...cartFlowProps}
              totalAmount={totalAmount}
              onPlaceOrder={onPlaceOrder}
              orderDescription={orderDescription}
              transferredPaymentDescription={transferredPaymentDescription}
              show={show}
              exhibitorPair={exhibitorPair}
              organiser={organiser}
              exhibitorUser={exhibitor}
            />
          )}
      </>
    );
  };

  return renderTabsView();
};

export default Payment;
